import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, Upload as UploadIcon, Check, AlertTriangle, Loader2, FileText, RefreshCw,
} from 'lucide-react'; // eslint-disable-line no-unused-vars
import {
  getAprovacoesUploadsRequired,
  validateAprovacaoFile,
  uploadAprovacoesBundle,
  uploadAprovacoesOv,
} from '../api';

const monoFont = `'JetBrains Mono', 'SF Mono', ui-monospace, Menlo, monospace`;

const TIPO_LABEL = {
  aprovacoes: 'Aprovações',
  pecas: 'Peças Faturadas',
  mao_obra: 'Mão de Obra',
  notas: 'Notas de Saída',
  ov: 'Relatório de OV (opcional)',
};

const TIPO_HINT = {
  aprovacoes: 'Planilha Sales Day / Aprovações que você já sobe',
  pecas: 'Relatório Faturadas — coluna J (OS), coluna C (Valor Total NF)',
  mao_obra: 'Relatório Mão de Obra — coluna B (OS), coluna E (Valor Total NF)',
  notas: 'Notas de saída — col L (Série=OS), col M (Nº Doc), col N (Valor)',
  ov: '"Relatório peça OV" do Care — traz o produto das vendas de seminovos (separa MX × In Home). Pode subir junto ou sozinho.',
};

function StatusBadge({ status, n_linhas, sample, erro }) {
  if (status === 'idle') {
    return (
      <span style={{ fontSize: 11, color: '#64748b', fontFamily: monoFont }}>
        — Aguardando arquivo —
      </span>
    );
  }
  if (status === 'validating') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#b45309', fontFamily: monoFont }}>
        <Loader2 size={11} /> Validando…
      </span>
    );
  }
  if (status === 'valid') {
    return (
      <div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#15803d', fontFamily: monoFont, fontWeight: 700 }}>
          <Check size={12} /> Validado — {n_linhas} linhas
        </span>
        {sample && sample.length > 0 && (
          <div style={{
            marginTop: 6, padding: '6px 8px', borderRadius: 6,
            background: 'rgba(52,211,153,0.05)',
            border: '1px solid rgba(52,211,153,0.18)',
            fontSize: 10.5, fontFamily: monoFont, color: '#15803d',
          }}>
            <div style={{ fontSize: 9.5, color: '#15803d', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>
              Amostra
            </div>
            {sample.slice(0, 3).map((s, i) => (
              <div key={i}>
                OS {s.os} → R$ {s.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  if (status === 'invalid') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#be123c', fontFamily: monoFont }}>
        <AlertTriangle size={12} /> {erro || 'Inválido'}
      </span>
    );
  }
  return null;
}

function DropSlot({ tipo, file, status, sample, n_linhas, erro, onFile }) {
  const handleChange = (e) => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
  };

  const accentColor = status === 'valid' ? '#34d399'
    : status === 'invalid' ? '#fda4af'
    : status === 'validating' ? '#fbbf24'
    : 'rgba(15,23,42,0.08)';

  return (
    <div style={{
      padding: 14, borderRadius: 12,
      background: 'rgba(15,23,42,0.08)',
      border: `1px solid ${accentColor}55`,
      marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <FileText size={16} color={accentColor} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
            {TIPO_LABEL[tipo] || tipo}
          </div>
          <div style={{ fontSize: 10.5, color: '#64748b' }}>
            {TIPO_HINT[tipo] || ''}
          </div>
        </div>
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 8,
          background: file ? 'rgba(15,23,42,0.04)' : 'linear-gradient(180deg, #60a5fa, #3b82f6)',
          border: `1px solid ${file ? 'rgba(15,23,42,0.10)' : 'rgba(96,165,250,0.6)'}`,
          color: file ? '#334155' : '#ffffff',
          fontSize: 11, fontWeight: 800, cursor: 'pointer',
          fontFamily: monoFont,
        }}>
          {file ? <RefreshCw size={11} /> : <UploadIcon size={11} />}
          {file ? 'Trocar' : 'Selecionar'}
          <input
            type="file"
            style={{ display: 'none' }}
            accept=".xls,.xlsx,.csv,.htm,.html"
            onChange={handleChange}
          />
        </label>
      </div>

      {file && (
        <div style={{
          marginTop: 4, padding: '6px 8px', borderRadius: 6,
          background: 'rgba(15,23,42,0.10)',
          border: '1px solid rgba(15,23,42,0.06)',
          fontSize: 11, color: '#334155', fontFamily: monoFont,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          📎 {file.name}
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <StatusBadge status={status} n_linhas={n_linhas} sample={sample} erro={erro} />
      </div>
    </div>
  );
}

export default function AprovacoesImportModal({ open, onClose, empresaSlug, ascCode, nomeLoja, onSuccess }) {
  const [requiredTipos, setRequiredTipos] = useState([]);
  const [opcionaisTipos, setOpcionaisTipos] = useState([]);   // ex.: JMV 'ov' (19/10/2026)
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [slots, setSlots] = useState({});  // { tipo: { file, status, n_linhas, sample, erro } }
  const [importing, setImporting] = useState(false);
  const [doneMsg, setDoneMsg] = useState(null);
  const [importErro, setImportErro] = useState(null);

  // Carrega requeridos quando abre
  useEffect(() => {
    if (!open || !empresaSlug) return;
    setLoadingConfig(true);
    setSlots({});
    setDoneMsg(null);
    setImportErro(null);
    getAprovacoesUploadsRequired(empresaSlug)
      .then(r => {
        const lista = r.data.uploads_required || ['aprovacoes'];
        const opc = r.data.uploads_opcionais || [];
        setRequiredTipos(lista);
        setOpcionaisTipos(opc);
        const init = {};
        [...lista, ...opc].forEach(t => { init[t] = { file: null, status: 'idle' }; });
        setSlots(init);
      })
      .catch(e => setImportErro('Erro carregando config: ' + (e.response?.data?.detail || e.message)))
      .finally(() => setLoadingConfig(false));
  }, [open, empresaSlug]);

  const onFile = useCallback(async (tipo, file) => {
    setSlots(s => ({ ...s, [tipo]: { file, status: 'validating' } }));
    // Aprovações não tem validate específico — assumimos valid se foi selecionado
    if (tipo === 'aprovacoes') {
      setSlots(s => ({ ...s, [tipo]: { file, status: 'valid', n_linhas: null, sample: [] } }));
      return;
    }
    try {
      const r = await validateAprovacaoFile(file, empresaSlug, tipo);
      const d = r.data;
      if (d.ok) {
        setSlots(s => ({ ...s, [tipo]: { file, status: 'valid', n_linhas: d.n_linhas, sample: d.sample || [] } }));
      } else {
        setSlots(s => ({ ...s, [tipo]: { file, status: 'invalid', erro: d.erro || 'Validação falhou' } }));
      }
    } catch (e) {
      setSlots(s => ({ ...s, [tipo]: { file, status: 'invalid', erro: e.response?.data?.detail || e.message } }));
    }
  }, [empresaSlug]);

  // Opcional preenchido precisa estar válido; opcional vazio não trava.
  const opcionaisOk = useMemo(() => opcionaisTipos.every(t => !slots[t]?.file || slots[t]?.status === 'valid'),
    [opcionaisTipos, slots]);
  const algumOpcional = opcionaisTipos.some(t => slots[t]?.status === 'valid');
  const nenhumObrigatorio = requiredTipos.every(t => !slots[t]?.file);
  // Envio SÓ do opcional (ex.: Relatório de OV) — os obrigatórios ficam vazios.
  const soOpcional = nenhumObrigatorio && algumOpcional && opcionaisOk;
  const allValid = useMemo(() => {
    if (requiredTipos.length === 0) return false;
    return requiredTipos.every(t => slots[t]?.status === 'valid') && opcionaisOk;
  }, [requiredTipos, slots, opcionaisOk]);
  const podeImportar = allValid || soOpcional;

  const handleImport = async () => {
    if (!podeImportar) return;
    setImporting(true);
    setImportErro(null);
    try {
      let r;
      if (soOpcional) {
        r = await uploadAprovacoesOv({ empresaSlug, ascCode, nomeLoja, file: slots.ov.file });
      } else {
        const files = {};
        requiredTipos.forEach(t => { files[t] = slots[t].file; });
        opcionaisTipos.forEach(t => { if (slots[t]?.status === 'valid') files[t] = slots[t].file; });
        r = await uploadAprovacoesBundle({ empresaSlug, ascCode, nomeLoja, files });
      }
      setDoneMsg(r.data);
      onSuccess && onSuccess(r.data);
    } catch (e) {
      setImportErro(e.response?.data?.detail || e.message);
    }
    setImporting(false);
  };

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 720, maxHeight: '92vh',
          background: '#ffffff',
          border: '1px solid rgba(15,23,42,0.08)',
          borderRadius: 14,
          display: 'flex', flexDirection: 'column',
          color: '#1e293b', overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '16px 20px',
          borderBottom: '1px solid rgba(15,23,42,0.08)',
          background: 'linear-gradient(180deg, rgba(96,165,250,0.10) 0%, transparent 100%)',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'rgba(96,165,250,0.22)',
            border: '1px solid rgba(96,165,250,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <UploadIcon size={18} color="#60a5fa" />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
              Importar Aprovações — {nomeLoja || empresaSlug}
            </h2>
            <p style={{ margin: '2px 0 0 0', fontSize: 11.5, color: '#64748b' }}>
              Suba os {requiredTipos.length} arquivos. O valor da OS é a soma das linhas auxiliares.
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(15,23,42,0.04)',
            border: '1px solid rgba(15,23,42,0.10)',
            borderRadius: 8, padding: 6, cursor: 'pointer',
            color: '#334155',
          }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {loadingConfig && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569', padding: 20 }}>
              <Loader2 size={16} /> Carregando configuração…
            </div>
          )}

          {!loadingConfig && !doneMsg && [...requiredTipos, ...opcionaisTipos].map(t => {
            const sl = slots[t] || { status: 'idle' };
            return (
              <DropSlot
                key={t}
                tipo={t}
                file={sl.file}
                status={sl.status}
                n_linhas={sl.n_linhas}
                sample={sl.sample}
                erro={sl.erro}
                onFile={(f) => onFile(t, f)}
              />
            );
          })}

          {importErro && (
            <div style={{
              marginTop: 8, padding: '10px 12px', borderRadius: 8,
              background: 'rgba(239,68,68,0.10)',
              border: '1px solid rgba(239,68,68,0.30)',
              color: '#dc2626', fontSize: 12,
            }}>
              <AlertTriangle size={12} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              {importErro}
            </div>
          )}

          {doneMsg && (
            <div style={{
              padding: 14, borderRadius: 12,
              background: 'rgba(52,211,153,0.08)',
              border: '1px solid rgba(52,211,153,0.30)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Check size={18} color="#34d399" />
                <span style={{ fontWeight: 800, color: '#0f172a', fontSize: 14 }}>Importado com sucesso!</span>
              </div>
              <div style={{ fontSize: 11.5, fontFamily: monoFont, color: '#15803d' }}>
                {Object.entries(doneMsg.resultados || {}).map(([t, r]) => (
                  <div key={t} style={{ marginBottom: 3 }}>
                    {TIPO_LABEL[t]}: {r.vendas_atualizadas != null
                      ? `${r.seminovos_no_arquivo} venda(s) de seminovo no arquivo, ${r.vendas_atualizadas} preenchida(s) com o produto`
                      : r.linhas_inseridas != null
                      ? `${r.linhas_inseridas} inseridas, ${r.linhas_substituidas || 0} substituídas`
                      : `${r.linhas_novas || 0} novas, ${r.linhas_atualizadas || 0} atualizadas`}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 20px',
          borderTop: '1px solid rgba(15,23,42,0.08)',
        }}>
          <span style={{ fontSize: 10.5, color: '#64748b', fontFamily: monoFont }}>
            {doneMsg
              ? 'Pronto — clique Fechar.'
              : soOpcional
                ? 'Só o Relatório de OV será enviado.'
                : `${requiredTipos.filter(t => slots[t]?.status === 'valid').length} de ${requiredTipos.length} validados`}
          </span>
          <div style={{ display: 'inline-flex', gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 14px', borderRadius: 8,
                background: 'rgba(15,23,42,0.04)',
                border: '1px solid rgba(15,23,42,0.10)',
                color: '#334155', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                fontFamily: monoFont,
              }}
            >
              {doneMsg ? 'Fechar' : 'Cancelar'}
            </button>
            {!doneMsg && (
              <button
                onClick={handleImport}
                disabled={!podeImportar || importing}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: 8,
                  background: podeImportar && !importing
                    ? 'linear-gradient(180deg, #34d399, #10b981)'
                    : 'rgba(15,23,42,0.04)',
                  border: `1px solid ${podeImportar && !importing ? 'rgba(52,211,153,0.6)' : 'rgba(15,23,42,0.10)'}`,
                  color: podeImportar && !importing ? '#ffffff' : '#94a3b8',
                  fontSize: 12, fontWeight: 800, cursor: podeImportar && !importing ? 'pointer' : 'not-allowed',
                  fontFamily: monoFont,
                }}
              >
                {importing ? <Loader2 size={12} /> : <Check size={12} />}
                {soOpcional ? 'Importar só o OV' : `Importar (${requiredTipos.filter(t => slots[t]?.status === 'valid').length}/${requiredTipos.length})`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
